import { defineStore } from 'pinia'
import ContractService from '@/api/ContractService.js'
import { useModalStore } from './modal'
import { useOptionsStore } from '@/stores/options'
import _ from 'lodash'
import { PRODUCT_TYPE_NAMES } from '@/data/constants'

export const useContractStore = defineStore('contract', {
    state: () => ({
        contract: null,
        contractData: null,
        contractId: null,
        clientId: null,
        contract_approved: 0,
        contract_user_approved: 0,
        isDeletable: null,
        billing_unit: '',

        // download pdf language component
        calculationPdfLanguage: '',

        loading: false,
        saving: false,
        saving_customer_uid: false,
        saving_temporary_contract_figure: false,
        deleting_temporary_contract_figure: false,
        requesting_approval: false,
        approving: false,
        downloading: false,
        saving_notes: false,
        saving_countersigning_date: false,
        sharing: false,
        unsharing: false,
    }),

    getters: {
        isPpuProductType() {
            return this.contractData?.product_type_name === PRODUCT_TYPE_NAMES.PPU
        },
    },

    actions: {
        _modalStore() {
            return useModalStore()
        },

        _optionsStore() {
            return useOptionsStore()
        },

        notification(severity, detail) {
            const summary = severity === 'error' ? 'Error' : 'Success'
            this.toast.add({
                severity,
                summary,
                detail,
                life: this.getToastDuration(detail, severity),
            })
        },

        async getContractData(contract) {
            this.loading = true
            try {
                const { data } = await ContractService.get(contract)
                const { contract_frontend_data } = data

                this.setContractData(contract_frontend_data)
            } catch (error) {
                console.error(
                    'Error fetching contract data (stores/contract/getContractData):',
                    error
                )
            } finally {
                this.loading = false
            }
        },

        setContractData(contract_frontend_data) {
            this.contractId = contract_frontend_data.id
            this.clientId = contract_frontend_data.client_id
            this.contract_approved = contract_frontend_data.approved
            this.contract_user_approved = contract_frontend_data.user_approved
            this.isDeletable = contract_frontend_data.allow_delete
            this.billing_unit = contract_frontend_data.measuring_unit
            this.contractData = contract_frontend_data
        },

        async updateCustomerUid(customerUid) {
            this.saving_customer_uid = true
            try {
                await ContractService.updateCustomerUid(
                    { contract: this.contractId },
                    { customer_uid: customerUid }
                )
                this.contractData.customer_uid = customerUid
                this.notification('success', '§ui_message.update_successful')
            } catch (error) {
                console.error(
                    'Error updating customer UID (stores/contract/updateCustomerUid):',
                    error
                )
                this.notification('error', '§ui_message.update_failed')
            } finally {
                this.saving_customer_uid = false
            }
        },

        async addTemporaryContractFigure(formData) {
            this.saving_temporary_contract_figure = true
            try {
                await ContractService.addTemporaryContractFigure(
                    { contract: this.contractId },
                    formData
                )
                this.notification('success', '§ui_message.save_successful')
            } catch (error) {
                console.error(
                    'Error creating temporary contract figure (stores/contract/addTemporaryContractFigure):',
                    error
                )
                this.notification('error', '§ui_message.save_failed')
            } finally {
                this.saving_temporary_contract_figure = false
            }
        },

        async deleteTemporaryContractFigure({ tcfId }) {
            this.deleting_temporary_contract_figure = true
            try {
                await ContractService.deleteTemporaryContractFigure({ tcf: tcfId })
                this.notification('success', '§ui_message.delete_successful')
            } catch (error) {
                console.error(
                    'Error deleting temporary contract figure (stores/contract/deleteTemporaryContractFigure):',
                    error
                )
                this.notification('error', '§ui_message.delete_failed')
            } finally {
                this.deleting_temporary_contract_figure = false
            }
        },

        async delete() {
            this.saving = true
            try {
                await ContractService.delete({ contract: this.contractId })
                this.router.push({ name: 'ContractIndex' })
                this.notification('success', '§ui_message.delete_successful')
            } catch (error) {
                console.error('Error deleting contract (stores/contract/delete):', error)
                this.notification('error', '§ui_message.delete_failed')
            } finally {
                this.saving = false
            }
        },

        async requestApproval() {
            if (this.requesting_approval) return
            this.requesting_approval = true
            try {
                await ContractService.requestApproval({ contract: this.contractId })
                this.notification('success', '§ui_message.approve_request_successful')
                this.contractData.user_approved = 1
            } catch (error) {
                console.error(
                    'Error requesting contract approval (stores/contract/requestApproval):',
                    error
                )
                this.notification('error', '§ui_message.approve_request_failed')
            } finally {
                this.requesting_approval = false
            }
        },

        async approve() {
            if (this.approving) return

            this.approving = true
            try {
                await ContractService.approve({ contract: this.contractId })
                this.notification('success', '§ui_message.approve_successful')
                this.getContractData(this.contractId)
            } catch (error) {
                console.error('Error approving contract (stores/contract/approve):', error)
                this.notification('error', '§ui_message.approve_failed')
            } finally {
                this.approving = false
            }
        },

        async update(formData) {
            this.saving = true
            try {
                await ContractService.update({ contract: this.contractId }, formData)
                this.notification('success', '§ui_message.update_successful')
                this.getContractData(this.contractId)
            } catch (error) {
                console.error('Error updating contract parameters (stores/contract/update):', error)
                this.notification('error', '§ui_message.update_failed')
            } finally {
                this.saving = false
            }
        },

        async updateNotes(notes) {
            this.saving_notes = true
            try {
                await ContractService.updateNotes(
                    { contract: this.contractId },
                    { notes }
                )
                this.contractData.notes = notes
                this.notification('success', '§ui_message.update_successful')
            } catch (error) {
                console.error(
                    'Error updating contract notes (stores/contract/updateNotes):',
                    error
                )
                this.notification('error', '§ui_message.update_failed')
            } finally {
                this.saving_notes = false
            }
        },

        async updateCountersigningDate(countersigningDate) {
            this.saving_countersigning_date = true
            try {
                await ContractService.updateCountersigningDate(
                    { contract: this.contractId },
                    { countersigning_date: countersigningDate }
                )
                this.contractData.countersigning_date = countersigningDate
                this.notification('success', '§ui_message.update_successful')
            } catch (error) {
                console.error(
                    'Error updating countersigning date (stores/contract/updateCountersigningDate):',
                    error
                )
                this.notification('error', '§ui_message.update_failed')
            } finally {
                this.saving_countersigning_date = false
            }
        },

        async previewShare({ contractIds, partnerId }) {
            const { data } = await ContractService.shareContractsPreview({
                contract_ids: contractIds,
                partner_id: partnerId,
            })
            return data
        },

        async shareContracts({ contractIds, partnerId, switchContractIds = [] }) {
            if (this.sharing) return
            this.sharing = true
            try {
                await ContractService.shareContracts({
                    contract_ids: contractIds,
                    partner_id: partnerId,
                    switch_contract_ids: switchContractIds,
                })
                this.notification('success', '§contracts.share_successful')
            } catch (error) {
                console.error('Error sharing contracts (stores/contract/shareContracts):', error)
                this.notification('error', '§contracts.share_failed')
                throw error
            } finally {
                this.sharing = false
            }
        },

        async unshareContract({ contractId, partnerId }) {
            if (this.unsharing) return
            this.unsharing = true
            try {
                await ContractService.unshareContract({
                    contract_id: contractId,
                    partner_id: partnerId,
                })
                this.notification('success', '§contracts.unshare_successful')
            } catch (error) {
                console.error('Error unsharing contract (stores/contract/unshareContract):', error)
                this.notification('error', '§contracts.unshare_failed')
                throw error
            } finally {
                this.unsharing = false
            }
        },

        async downloadPdf() {
            this.downloading = true
            try {
                this._modalStore().setClose()
                await ContractService.downloadPdf({
                    locale: this.calculationPdfLanguage,
                    contract: this.contractId,
                })
            } catch (error) {
                console.error(
                    'Error downloading contract PDF (stores/contract/downloadPdf):',
                    error
                )
                this.notification('error', '§ui_message.update_failed')
            } finally {
                this.downloading = false
            }
        },
    },
})
